class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search')
    this.isOpen = false;
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');
    this.productTypeFilter = this.querySelector('form.search .field .search-type-selector select.product-type-dropdown');
    this.typingTimer = null;

    // Clear input value on page load
    if (this.input) {
      this.input.value = '';
    }

    this.setupEventListeners();
    this.quickSearchTrending.forEach(element => {
      element.addEventListener('click', this.quickSearchTrendingClick.bind(this));
    });
  }
  
  setupEventListeners() {
    const searchForm = this.querySelector('form.search');
    searchForm.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', (event) => {
      this.onChange(event);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }

    if (this.productTypeFilter) {
      this.productTypeFilter.addEventListener('change', () => {
        const searchTerm = this.getQuery();
        const selectedType = this.productTypeFilter.value.trim();
        
        // Clear cache when product type changes
        this.cachedResults = {};
        
        if (searchTerm.length) {
          const searchTermWithWildcard = this.getSearchTermWithWildcard(searchTerm, selectedType);

          console.log('Product type changed:', {
            searchTerm: searchTermWithWildcard,
            originalTerm: searchTerm,
            productType: selectedType,
            hasFilter: Boolean(selectedType && selectedType.toLowerCase() !== 'all'),
            cacheCleared: true
          });

          this.getSearchResults(searchTermWithWildcard);
        } else {
          console.log('Product type changed:', {
            searchTerm: '',
            productType: selectedType,
            hasFilter: Boolean(selectedType && selectedType.toLowerCase() !== 'all'),
            cacheCleared: true,
            action: 'show_trending'
          });
          
          this.showTrendingAndProducts();
        }
      });
    } else {
      console.warn('Product type filter not found in DOM');
    }
  }

  // Helper method to get search term with wildcard if needed
  getSearchTermWithWildcard(searchTerm, productType) {
    // Add wildcard to any search term with 3 or fewer characters
    return searchTerm.length <= 3 ? `${searchTerm}*` : searchTerm;
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    const productType = this.productTypeFilter ? this.productTypeFilter.value.trim() : '';
    
    // Clear any existing timer
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }

    if (!searchTerm.length) {
      // Clear search results immediately
      this.predictiveSearchResults.innerHTML = '';
      this.removeAttribute('results');
      this.close(true);
      this.showTrendingAndProducts();
      return;
    }

    // Keep existing results visible while typing
    if (!this.predictiveSearchResults.innerHTML) {
      this.open();
    }

    // Set new timer to wait for typing to finish
    this.typingTimer = setTimeout(() => {
      const searchTermWithWildcard = this.getSearchTermWithWildcard(searchTerm, productType);

      console.log('Search input changed:', {
        searchTerm: searchTermWithWildcard,
        originalTerm: searchTerm,
        productType,
        hasFilter: Boolean(productType && productType.toLowerCase() !== 'all'),
        delay: '1000ms'
      });

      this.getSearchResults(searchTermWithWildcard);
    }, 1000); // Wait 1 second after last keystroke before searching
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();
    const productType = this.productTypeFilter ? this.productTypeFilter.value.trim() : '';
    
    // Add wildcard to search term if needed - declare at method scope
    const searchTermWithWildcard = this.getSearchTermWithWildcard(searchTerm, productType);
    
    if (!searchTerm.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    const form = event.target;
    const searchInput = form.querySelector('input[type="search"]');
    
    if (searchInput) {
      // Create hidden input for search query with wildcards and product type
      const searchTermInput = document.createElement('input');
      searchTermInput.type = 'hidden';
      searchTermInput.name = 'q';
      
      // Set search term input value (without product type)
      searchTermInput.value = searchTermWithWildcard;
      
      // Add product type filter as a separate parameter if needed
      if (productType && productType.toLowerCase() !== 'all') {
        const cleanProductType = productType.replace(/"/g, '');
        const filterInput = document.createElement('input');
        filterInput.type = 'hidden';
        filterInput.name = 'filter.p.product_type';
        filterInput.value = cleanProductType;
        form.appendChild(filterInput);
      }
      
      form.appendChild(searchTermInput);
      
      // Create hidden input for original search term
      const originalInput = document.createElement('input');
      originalInput.type = 'hidden';
      originalInput.name = 'original_q';
      originalInput.value = searchTerm;
      form.appendChild(originalInput);
      
      // Clear and remove name from visible input
      // searchInput.value = '';  // Commented out to keep search term visible
      searchInput.name = '';
    }

    // Add resources type for collections
    const resourceTypeInput = document.createElement('input');
    resourceTypeInput.type = 'hidden';
    resourceTypeInput.name = 'resources[type]';
    resourceTypeInput.value = 'product,collection';
    form.appendChild(resourceTypeInput);

    // Log form submission details
    console.log('Form submission:', {
      searchTerm: searchTermWithWildcard,
      originalTerm: searchTerm,
      productType,
      hasFilter: Boolean(productType && productType.toLowerCase() !== 'all'),
      formAction: form.action,
      formParams: new URLSearchParams(new FormData(form)).toString()
    });
  }

  onFocus() {
    const searchTerm = this.getQuery();
    const productType = this.productTypeFilter ? this.productTypeFilter.value.trim() : '';
    
    if (!searchTerm.length) {
      this.showTrendingAndProducts();
      return;
    }

    const searchTermWithWildcard = this.getSearchTermWithWildcard(searchTerm, productType);
    
    // Cache key should be based on the full query
    const queryKey = `${searchTermWithWildcard}-${productType}`.replace(/\s+/g, "-").toLowerCase();
    console.log('Focus search:', {
      searchTerm: searchTermWithWildcard,
      originalTerm: searchTerm,
      productType,
      hasFilter: Boolean(productType && productType.toLowerCase() !== 'all'),
      useCache: Boolean(this.getAttribute('results') === 'true' && this.cachedResults[queryKey])
    });

    if (this.getAttribute('results') === 'true' && this.cachedResults[queryKey]) {
      this.open();
    } else {
      this.getSearchResults(searchTermWithWildcard);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        // Check if this is the modal sidebar search and has search results
        const isModalSearch = this.input.id === 'Search-In-Modal-Sidebar';
        const hasSearchResults = this.predictiveSearchResults.innerHTML.trim() !== '';
        const searchTerm = this.getQuery();

        if (!isModalSearch || !hasSearchResults || !searchTerm) {
          this.close();
        }
        // Don't hide trending products for modal search with results
        if (!isModalSearch) {
          this.hideTrendingAndProducts();
        }
      };
    })
  }
  
  onKeyup(event) {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) {
      // Clear search results immediately
      this.predictiveSearchResults.innerHTML = '';
      this.removeAttribute('results');
      this.close(true);
      this.showTrendingAndProducts();
    }
    
    event.preventDefault();
    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }
    
    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
    
  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }
  
  getSearchResults(searchTerm) {
    const productType = this.productTypeFilter ? this.productTypeFilter.value.trim() : '';
    
    const searchTermWithWildcard = this.getSearchTermWithWildcard(searchTerm, productType);
    
    // Cache key should be based on search term and product type
    const queryKey = `${searchTermWithWildcard}-${productType}`.replace(/\s+/g, "-").toLowerCase();
    
    // Only show loading state if there are no existing results
    if (!this.predictiveSearchResults.innerHTML) {
      this.setLiveRegionLoadingState();
    }
    
    if (this.cachedResults[queryKey]) {
      console.log('Search results:', {
        searchTerm: searchTermWithWildcard,
        originalTerm: searchTerm,
        productType,
        hasFilter: Boolean(productType && productType.toLowerCase() !== 'all'),
        source: 'cache',
        queryKey
      });
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTermWithWildcard, productType);
      return;
    }

    console.log('Search results:', {
      searchTerm: searchTermWithWildcard,
      originalTerm: searchTerm,
      productType,
      hasFilter: Boolean(productType && productType.toLowerCase() !== 'all'),
      source: 'api',
      queryKey
    });

    // Search parameters
    const searchParams = new URLSearchParams();
    
    // For predictive search API, we need to include product type in query
    if (productType && productType.toLowerCase() !== 'all') {
      // Remove any existing quotes from product type
      const cleanProductType = productType.replace(/"/g, '');
      // Set search query with product type filter
      searchParams.set('q', `${searchTerm} product_type:"${cleanProductType}"`);
    } else {
      searchParams.set('q', searchTerm);
    }
    
    searchParams.append('resources[type]', 'product,collection,query');
    searchParams.append('resources[options][unavailable_products]', 'show');
    searchParams.append('resources[options][fields]', 'title,product_type,variants.title');
    searchParams.append('resources[limit_scope]', 'each');
    searchParams.append('resources[limit]', '10');
    searchParams.append('section_id', 'predictive-search');

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        
        // Cache and render the results
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        this.updateViewAllLink(searchTermWithWildcard, productType);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }
  
  setLiveRegionLoadingState() {
    const statusElement = this.querySelector('.predictive-search-status');
    if (statusElement) {
      this.statusElement = statusElement;
      this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      
      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 500);
    }
  }

  renderSearchResults(resultsMarkup) {
    // Parse the markup into a DOM element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = resultsMarkup;
    
    // Get the search term
    const searchTerm = this.getQuery().trim().toLowerCase();
    
    // Find all suggestion text elements and enhance highlighting
    const suggestionTexts = tempDiv.querySelectorAll('.predictive-search__query-text');
    suggestionTexts.forEach(element => {
      const text = element.textContent;
      if (searchTerm && text) {
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        element.innerHTML = text.replace(regex, '<strong>$1</strong>');
      }
    });
    
    // Update the DOM with the modified content
    this.predictiveSearchResults.innerHTML = tempDiv.innerHTML;
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();

    // Setup tab click handlers after content is rendered
    const productsTab = this.querySelector('#predictive-search-products');
    const collectionsTab = this.querySelector('#predictive-search-collections');
    const productsGrid = this.querySelector('#predictive-search-results-products');
    const collectionsSection = this.querySelector('#predictive-search-results-collections');

    if (productsTab && collectionsTab && productsGrid && collectionsSection) {
      // Products tab click handler
      productsTab.addEventListener('click', () => {
        productsGrid.removeAttribute('hidden');
        collectionsSection.setAttribute('hidden', '');
        productsTab.setAttribute('aria-selected', 'true');
        collectionsTab.setAttribute('aria-selected', 'false');
      });

      // Collections tab click handler
      collectionsTab.addEventListener('click', () => {
        productsGrid.setAttribute('hidden', '');
        collectionsSection.removeAttribute('hidden');
        collectionsTab.setAttribute('aria-selected', 'true');
        productsTab.setAttribute('aria-selected', 'false');
      });

      // Check if .fdp-new-search has .fdp-new-search-collections-first class
      const fdpNewSearch = document.querySelector('.fdp-new-search');
      const hasCollectionsFirst = fdpNewSearch && fdpNewSearch.classList.contains('fdp-new-search-collections-first');
      
      // Check if there are actual collection results - look for collection items more specifically
      const collectionItems = collectionsSection ? collectionsSection.querySelectorAll('[data-predictive-search-type="collection"], .predictive-search__item--collection, li') : [];
      const hasCollectionResults = collectionItems.length > 0;
      
      // Debug logging
      console.log('Collections first check:', {
        fdpNewSearch: !!fdpNewSearch,
        hasCollectionsFirstClass: hasCollectionsFirst,
        collectionsSection: !!collectionsSection,
        collectionItemsCount: collectionItems.length,
        hasCollectionResults,
        shouldShowCollectionsFirst: hasCollectionsFirst && hasCollectionResults
      });
      
      // Initialize tab selection based on conditions
      if (hasCollectionsFirst && hasCollectionResults) {
        // Show collections first
        console.log('Showing collections first');
        collectionsTab.setAttribute('aria-selected', 'true');
        productsTab.setAttribute('aria-selected', 'false');
        collectionsSection.removeAttribute('hidden');
        productsGrid.setAttribute('hidden', '');
      } else {
        // Default: show products first
        console.log('Showing products first (default)');
        productsTab.setAttribute('aria-selected', 'true');
        collectionsTab.setAttribute('aria-selected', 'false');
        productsGrid.removeAttribute('hidden');
        collectionsSection.setAttribute('hidden', '');
      }
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    } else {
      this.setLiveRegionText('');
    }
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('[class^="header-navigation"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
  
  open() {
    //this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      // Clear search results
      this.predictiveSearchResults.innerHTML = '';
      // Reset product type filter when search is cleared
      if (this.productTypeFilter) {
        this.productTypeFilter.value = '';
        this.cachedResults = {};
      }
    }

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;

    // Keep aria-selected states
    const productsTab = this.querySelector('#predictive-search-products');
    const collectionsTab = this.querySelector('#predictive-search-collections');
    const productsGrid = this.querySelector('#predictive-search-results-products');
    const collectionsSection = this.querySelector('#predictive-search-results-collections');

    if (productsTab && collectionsTab && productsGrid && collectionsSection) {
      // Keep the current tab states
      if (productsTab.getAttribute('aria-selected') === 'true') {
        productsGrid.removeAttribute('hidden');
        collectionsSection.setAttribute('hidden', '');
      } else if (collectionsTab.getAttribute('aria-selected') === 'true') {
        productsGrid.setAttribute('hidden', '');
        collectionsSection.removeAttribute('hidden');
      }
    }

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 
    this.trendingAndProductsBlock.classList.add('is-show')
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 

    this.trendingAndProductsBlock.classList.remove('is-show')
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target), $target = e.target;
    const isModalSearch = this.input.id === 'Search-In-Modal-Sidebar';
    const hasSearchResults = this.predictiveSearchResults.innerHTML.trim() !== '';
    const searchTerm = this.getQuery();
    const isCloseButton = $target.closest('.header-search-popup-close') || 
                         $target.matches('.header-search-popup-close') || 
                         $target.closest('.header-search-close') || 
                         $target.matches('.header-search-close');

    // Always close if close button is clicked
    if (isCloseButton) {
      this.close();
      this.hideTrendingAndProducts();
    } 
    // For modal search with results, only close if clicking inside search area
    else if (!isInModal && (!isModalSearch || !hasSearchResults || !searchTerm)) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open')
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open')
  }

  updateViewAllLink(searchTerm, productType) {
    const qsViewAllLink = document.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink) return 

    const searchTermWithWildcard = this.getSearchTermWithWildcard(searchTerm, productType);
    
    const searchParams = new URLSearchParams({
      'resources[type]': 'product,collection',
      'original_q': searchTerm
    });
    
    // Set search query (without product type)
    searchParams.set('q', searchTermWithWildcard);
    
    // Add product type filter as a separate parameter if needed
    if (productType && productType.toLowerCase() !== 'all') {
      // Remove any existing quotes from product type
      const cleanProductType = productType.replace(/"/g, '');
      searchParams.append('filter.p.product_type', cleanProductType);
    }

    const linkTotal = `${routes.search_url}?${searchParams.toString()}`;

    console.log('View All link:', {
      searchTerm: searchTermWithWildcard,
      originalTerm: searchTerm,
      productType,
      hasFilter: Boolean(productType && productType.toLowerCase() !== 'all'),
      url: linkTotal,
      params: searchParams.toString()
    });
    
    qsViewAllLink.href = linkTotal;
    return this.getTotalResults(linkTotal).then(count => {
      console.log('View All results:', {
        searchTerm: searchTermWithWildcard,
        originalTerm: searchTerm,
        productType,
        hasFilter: Boolean(productType && productType.toLowerCase() !== 'all'),
        count,
        url: linkTotal
      });
      qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace('()', `(${count})`);
    });
  }

  getTotalResults(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })  
      .then((text) => {
        const totalCount = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]').dataset.searchCount;
        return totalCount
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);
